import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PortalRing from '../stories/PortalRing';
import { ringCity, cityTone } from '../stories/reelRing';
import { SearchIcon } from '../hand/icons';
import { discoFrame, fillFor } from '../wall/wallTokens';
import { T, RADIUS, formatTime, previewText } from './messagesModel';
import { flipRows, popBadge } from './messagesMotion';
import { LockIcon } from './SecureLockSheet';
import { EMPTY_BODY, LOCK_TYPE_LABEL, mmss } from './secureModel';
import { TAB_BAR_CLEARANCE } from '../MobileTabBar';
import { useHideTabBarOnScroll } from '../chrome/tabBarVisibility';

/**
 * The inbox column (handoff: "Inbox column"): a white column with no rules — the header
 * block (MESSAGES, +, the search pill, the filter pills), the Portals rail, the thread
 * rows as cards, and the footer line.
 */
export default function InboxColumn({
  threads,
  activeId,
  onSelect,
  onDelete,
  onMarkUnread,
  onViewProfile,
  filter,
  setFilter,
  search,
  setSearch,
  loading,
  onNewMessage,
  /** { personalUnread, pages: [{ id, name, slug, logoUrl, role, unread }] } — the switcher beside the title. */
  inboxes = null,
  /** The Pro page the inbox is showing, or null for your own. */
  activePageId = null,
  viewer = null,
  onSwitchInbox,
  unreadTotal = 0,
  rail = [],
  railLive = 0,
  ringFor,
  onOpenReel,
  onSendOne,
  isMobile = false,
  /** Secure chats (useSecureChats): the view, the lock, the counts. Null disables the feature. */
  secure = null,
  onMoveToSecure,
  onRemoveFromSecure,
  onOpenSettings,
}) {
  const [searchFocused, setSearchFocused] = useState(false);
  const scrollerRef = useRef(null);
  // On a phone the list runs under the floating tab bar (white to the bottom edge); the bar
  // slides away while the list is scrolled down and back on the way up. The clearance inside
  // the scroller keeps the last row reachable above the bar when it is showing.
  useHideTabBarOnScroll(scrollerRef, { enabled: isMobile });
  // Where every row sat after the last commit, by thread id — the "before" of the next FLIP.
  const positions = useRef(new Map());
  const unreadBefore = useRef(new Map());

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const rows = Array.from(scroller.querySelectorAll('[data-tid]'));
    const before = positions.current;
    if (before.size) {
      // The bumped row is whichever now sits first having been somewhere else.
      const first = rows[0];
      const bumpedId = first && before.has(first.dataset.tid) && before.get(first.dataset.tid) !== first.offsetTop ? first.dataset.tid : null;
      flipRows({ rows, before, bumpedId });
    }
    const next = new Map();
    rows.forEach((el) => next.set(el.dataset.tid, el.offsetTop));
    positions.current = next;

    // Unread badges pop when their count goes up.
    const seen = unreadBefore.current;
    const nextUnread = new Map();
    threads.forEach((t) => {
      nextUnread.set(t.id, t.unreadCount || 0);
      const was = seen.get(t.id) || 0;
      if ((t.unreadCount || 0) > was && seen.size) {
        const badge = scroller.querySelector(`[data-tid="${t.id}"] [data-badge]`);
        if (badge) popBadge(badge);
      }
    });
    unreadBefore.current = nextUnread;
  }, [threads]);

  const pills = [
    ['all', 'ALL'], ['friends', 'FRIENDS'], ['hosts', 'HOSTS'], ['unread', `UNREAD · ${unreadTotal}`],
  ];

  const searchStyle = {
    display: 'flex', alignItems: 'center', height: 36, borderRadius: RADIUS.pill,
    ...(searchFocused
      ? discoFrame({ fill: T.white })
      : search
        ? { border: '2px solid transparent', background: fillFor('open') }
        : { border: '2px solid transparent', background: T.off }),
  };
  if (searchFocused) searchStyle.borderRadius = RADIUS.pill;

  const ringSize = isMobile ? 64 : 56;
  const inVault = secure?.view === 'secure';
  const activePage = activePageId ? (inboxes?.pages || []).find((p) => p.id === activePageId) || null : null;

  // The PORTALS rail. Pinned above the list on desktop; on a phone it is the first thing
  // INSIDE the list, so it scrolls away with the threads instead of holding a band of screen.
  const railBlock = !inVault && (
    <div data-rail-block className="flex-shrink-0" style={{ padding: '12px 0 14px' }}>
      <div className="flex justify-between items-baseline" style={{ padding: '0 16px 10px' }}>
        <span className="font-mono font-bold" style={{ fontSize: 12, letterSpacing: '.05em', color: T.grey500 }}>PORTALS</span>
        <span className="font-mono" style={{ fontSize: 10, letterSpacing: '.05em', color: T.grey400 }}>{railLive} LIVE</span>
      </div>
      <div data-scroll="rail" className="flex items-start overflow-x-auto pr-rail-scroller" style={{ gap: 12, padding: '12px 16px 8px' }}>
        <PortalRing state="send" size={ringSize} label="OPEN ONE" onClick={onSendOne} ariaLabel="Open a Portal" />
        {rail.map((reel) => {
          const ring = ringFor?.(reel.authorUserId);
          if (!ring) return null;
          return (
            <PortalRing
              key={reel.authorUserId}
              {...ring}
              size={ringSize}
              city={ringCity(reel)}
              cityTone={cityTone(reel)}
              label={reel.isOwn ? 'YOU' : (reel.handle || reel.displayName || '').toUpperCase()}
              onClick={(e) => onOpenReel?.(reel.authorUserId, e.portalRingRect || e.currentTarget?.getBoundingClientRect?.())}
              ariaLabel={reel.isOwn ? 'Your Portal' : `Portal from ${reel.handle || reel.displayName}`}
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: T.white }}>
      <div className="flex-shrink-0" style={{ padding: 16 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12, gap: 10 }}>
          <h2 className="font-mono font-bold uppercase flex-shrink-0" style={{ fontSize: 14, letterSpacing: '.1em' }}>MESSAGES</h2>
          {inboxes?.pages?.length > 0 && (
            <InboxSwitcher inboxes={inboxes} activePageId={activePageId} viewer={viewer} onSwitch={onSwitchInbox} isMobile={isMobile} />
          )}
          {secure && <SecureLockButton secure={secure} grow={!(inboxes?.pages?.length > 0)} />}
          {onOpenSettings && (
            <button
              type="button"
              data-messages-settings
              onClick={onOpenSettings}
              title="Settings"
              aria-label="Messages settings"
              className="flex items-center justify-center flex-shrink-0"
              style={{ width: 36, height: 36, boxSizing: 'border-box', borderRadius: '50%', border: '2px solid transparent', background: T.off, color: T.ink, marginLeft: 4, cursor: 'pointer' }}
            >
              <GearIcon size={16} />
            </button>
          )}
          <button
            type="button"
            data-new-message
            onClick={(e) => onNewMessage?.(e.currentTarget.getBoundingClientRect())}
            title="New message"
            aria-label="New message"
            className="flex items-center justify-center hover:bg-white hover:text-black"
            style={{ width: 36, height: 36, borderRadius: '50%', background: T.ink, color: T.white, border: `2px solid ${T.ink}`, transition: 'background-color 200ms, color 200ms' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M5 12h14M12 5v14" /></svg>
          </button>
        </div>
        {activePage && (
          <div data-sending-as className="flex items-center font-mono font-bold" style={{ marginBottom: 10, gap: 8, fontSize: 9, letterSpacing: '.1em', color: T.grey600 }}>
            <span className="truncate">SENDING AS {activePage.name.toUpperCase()}</span>
            <button type="button" onClick={() => onSwitchInbox?.(null)} className="flex-shrink-0 hover:underline" style={{ color: T.ink }}>BACK TO YOU</button>
          </div>
        )}
        <label style={searchStyle}>
          <span className="flex" style={{ paddingLeft: 12, color: T.ink }}><SearchIcon size={15} /></span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder={inVault ? 'SEARCH SECURE CHATS' : 'SEARCH FRIENDS / HOSTS'}
            aria-label="Search conversations"
            className="min-w-0 flex-1 border-0 bg-transparent font-mono uppercase placeholder:text-gray-400 focus:outline-none focus:ring-0"
            style={{ fontSize: 12, letterSpacing: '.05em', padding: '0 14px 0 6px', height: 32 }}
          />
        </label>
        {inVault && <VaultBar secure={secure} />}
        {!inVault && <div className="flex flex-wrap" style={{ gap: 8, marginTop: 10 }}>
          {pills.map(([key, label]) => {
            const on = filter === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(on && key !== 'all' ? 'all' : key)}
                className="font-mono font-bold whitespace-nowrap"
                style={{
                  height: 32, padding: '0 14px', borderRadius: RADIUS.pill, border: '2px solid transparent',
                  background: fillFor(on ? 'on' : 'off'), color: on ? T.white : T.ink, fontSize: 10, letterSpacing: '.06em',
                  transition: 'color 200ms',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>}
      </div>

      {/* On a phone the rail scrolls with the threads — it is the first thing in the list,
          not a band pinned above it, so the conversations get the screen back. */}
      {!isMobile && railBlock}

      <div ref={scrollerRef} data-scroll="rows" className="flex-1 min-h-0 overflow-y-auto" style={{ position: 'relative', paddingBottom: isMobile && !inVault ? TAB_BAR_CLEARANCE : 0 }}>
        {isMobile && railBlock}
        {loading && (
          <div className="p-6 text-center font-mono text-xs uppercase tracking-widest" style={{ color: T.grey400 }}>…</div>
        )}
        {!loading && threads.length === 0 && !inVault && (
          <div className="p-6 text-center font-mono text-xs uppercase tracking-widest" style={{ color: T.grey400 }}>NO THREADS</div>
        )}
        {!loading && threads.length === 0 && inVault && (
          <div data-secure-empty className="text-center" style={{ padding: '40px 28px' }}>
            <div className="font-mono font-bold" style={{ fontSize: 11, letterSpacing: '.1em', color: T.grey400 }}>NO SECURE CHATS</div>
            {!search && <p style={{ margin: '10px 0 0', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif', fontSize: 13, lineHeight: 1.45, color: T.grey500, textWrap: 'pretty' }}>{EMPTY_BODY}</p>}
          </div>
        )}
        {!loading && threads.map((t) => (
          <ThreadRow
            key={t.id}
            thread={t}
            active={t.id === activeId}
            ring={!t.isGroup && t.peer?.userId ? ringFor?.(t.peer.userId) : null}
            onOpen={(originEl) => onSelect(t.id, originEl)}
            onDelete={() => onDelete(t.id)}
            onMarkUnread={() => onMarkUnread(t.id)}
            onViewProfile={() => onViewProfile(t)}
            secureView={inVault}
            onMoveToSecure={secure && !inVault ? () => onMoveToSecure?.(t.id) : null}
            onRemoveFromSecure={secure && inVault ? () => onRemoveFromSecure?.(t.id) : null}
          />
        ))}
      </div>

      {/* The vault says what it is; the ordinary list needs no footer (the scroller carries
          the bottom clearance on a phone, the safe area with it). */}
      {inVault ? (
        <div className={`flex-shrink-0 p-2 ${isMobile ? '' : 'pb-safe-bot'} font-mono text-center uppercase`} style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey400, ...(isMobile ? { paddingBottom: TAB_BAR_CLEARANCE } : {}) }}>
          HIDDEN FROM THREADS · LOCKS AFTER {secure.windowMinutes} MIN
        </div>
      ) : !isMobile ? (
        <div className="flex-shrink-0 pb-safe-bot" />
      ) : null}
    </div>
  );
}

/** One conversation: a rounded card with the person's ring (or photo), name, preview and badge. */
export function ThreadRow({ thread, active, ring, onOpen, onDelete, onMarkUnread, onViewProfile, secureView = false, onMoveToSecure = null, onRemoveFromSecure = null }) {
  const last = thread.lastMessagePreview;
  // The ⋯ menu: where it sits on screen. It is portalled to the body because the row
  // card clips its overflow (the rounded corners, the swipe), which cut the menu off at
  // the row's bottom edge.
  const [menuAt, setMenuAt] = useState(null);   // { top, right }
  const menuOpen = !!menuAt;
  const [swiped, setSwiped] = useState(false);
  const menuRef = useRef(null);
  const menuPanelRef = useRef(null);
  const avRef = useRef(null);
  const touch = useRef({ x0: 0, dx: 0 });

  const toggleMenu = (e) => {
    if (menuAt) { setMenuAt(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setMenuAt({ top: r.bottom + 2, right: Math.max(8, window.innerWidth - r.right) });
  };
  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDoc(e) {
      if (menuRef.current?.contains(e.target) || menuPanelRef.current?.contains(e.target)) return;
      setMenuAt(null);
    }
    // The list scrolling under an anchored menu would leave it floating: close it.
    const close = () => setMenuAt(null);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuOpen]);

  // A swipe is a gesture that STARTED sideways. The axis is decided from the first few
  // pixels and then held: a fast scroll that drifts left along the way stays a scroll,
  // where it used to open the actions on whichever row the thumb left last.
  const onTouchStart = (e) => { touch.current = { x0: e.touches[0].clientX, y0: e.touches[0].clientY, dx: 0, axis: null }; };
  const onTouchMove = (e) => {
    const t = touch.current;
    const dx = e.touches[0].clientX - t.x0;
    const dy = e.touches[0].clientY - t.y0;
    if (!t.axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) t.axis = Math.abs(dx) > Math.abs(dy) * 1.5 ? 'x' : 'y';
    if (t.axis === 'x') t.dx = dx;
  };
  const onTouchEnd = () => {
    const t = touch.current;
    if (t.axis !== 'x') return;
    if (t.dx < -40) setSwiped(true);
    else if (t.dx > 40) setSwiped(false);
  };

  const canViewProfile = !thread.isGroup && thread.peer?.userId;
  const menuItems = [
    { label: 'MARK AS UNREAD', act: onMarkUnread },
    ...(onMoveToSecure ? [{ label: 'MOVE TO SECURE', act: onMoveToSecure }] : []),
    ...(onRemoveFromSecure ? [{ label: 'REMOVE FROM SECURE', act: onRemoveFromSecure }] : []),
    ...(canViewProfile ? [{ label: 'VIEW PROFILE', act: onViewProfile }] : []),
    { label: 'DELETE CONVERSATION', act: onDelete, danger: true },
  ];
  const secureAct = onMoveToSecure || onRemoveFromSecure;
  const unread = (thread.unreadCount || 0) > 0;
  const fg = active ? T.white : T.ink;
  const initial = (thread.peer?.displayName || '?').charAt(0).toUpperCase();

  return (
    <div
      data-tid={thread.id}
      className="group relative"
      style={{ margin: '2px 8px', borderRadius: RADIUS.row, overflow: 'hidden', background: active ? T.ink : T.white, color: fg, transition: 'background-color 200ms' }}
    >
      {/* Swipe-revealed actions (phone), sitting behind the row. Mounted only while swiped:
          left in place, the red button's anti-aliased edge bled through the card's rounded
          clip as a pink arc on every row. */}
      {swiped && <div className="absolute inset-y-0 right-0 flex sm:hidden">
        <button type="button" onClick={() => { setSwiped(false); onMarkUnread(); }} className="w-16 font-mono text-[9px] font-bold uppercase tracking-widest" style={{ background: T.ink, color: T.white }}>Unread</button>
        {secureAct && (
          <button type="button" onClick={() => { setSwiped(false); secureAct(); }} className="w-16 font-mono text-[9px] font-bold uppercase tracking-widest" style={{ background: T.ink, color: T.white, borderLeft: '1px solid rgba(255,255,255,.2)' }}>{onRemoveFromSecure ? 'Unsecure' : 'Secure'}</button>
        )}
        <button type="button" onClick={() => { setSwiped(false); onDelete(); }} className="w-16 font-mono text-[9px] font-bold uppercase tracking-widest" style={{ background: T.danger, color: T.white }}>Delete</button>
      </div>}

      <div
        className="relative flex items-stretch transition-transform"
        style={{ transform: swiped ? `translateX(-${secureAct ? 192 : 128}px)` : 'translateX(0)', background: active ? T.ink : T.white }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button
          type="button"
          onClick={() => (swiped ? setSwiped(false) : onOpen(avRef.current))}
          className="flex min-w-0 flex-1 items-start text-left"
          style={{ gap: 12, padding: '10px 10px', color: fg }}
        >
          <span ref={avRef} data-av className="relative flex-shrink-0 flex items-center justify-center" style={{ width: 56, height: 56 }}>
            {ring ? (
              <PortalRing {...ring} size={56} />
            ) : (
              <span className="flex items-center justify-center overflow-hidden font-mono font-bold text-black" style={{ width: 44, height: 44, borderRadius: RADIUS.photo, background: T.activeBadge, fontSize: 14 }}>
                {thread.peer?.avatarUrl ? <img src={thread.peer.avatarUrl} alt="" className="w-full h-full object-cover" /> : initial}
              </span>
            )}
          </span>
          <span className="min-w-0 flex-1 block" style={{ paddingTop: 6 }}>
            <span className="flex justify-between" style={{ gap: 6 }}>
              <span className="flex items-center min-w-0" style={{ gap: 6 }}>
                {secureView && <LockIcon size={11} style={{ flexShrink: 0 }} />}
                <span className="truncate font-mono font-bold uppercase" style={{ fontSize: 13, letterSpacing: '.05em' }}>{thread.peer?.displayName}</span>
              </span>
              <span className="flex-shrink-0 font-mono" style={{ fontSize: 10, letterSpacing: '.1em', color: active ? 'rgba(255,255,255,.6)' : T.grey400 }}>{formatTime(thread.lastMessageAt)}</span>
            </span>
            {thread.eventTitle && (
              <span className="block truncate font-mono font-bold" style={{ marginTop: 2, fontSize: 10, letterSpacing: '.1em', color: active ? T.activeBadge : T.grey500 }}>
                HOST · {thread.eventTitle}
              </span>
            )}
            {thread.peer?.subLabel && (
              <span className="block truncate font-mono font-bold" style={{ marginTop: 2, fontSize: 10, letterSpacing: '.1em', color: active ? 'rgba(255,255,255,.6)' : T.grey500 }}>
                WITH {thread.peer.subLabel.toUpperCase()}
              </span>
            )}
            <span className="block truncate" style={{ marginTop: 4, fontSize: 12, fontWeight: unread && !active ? 700 : 400, color: active ? 'rgba(255,255,255,.7)' : unread ? T.ink : T.grey500 }}>
              {previewText(last) || '…'}
            </span>
          </span>
          {unread && (
            <span
              data-badge
              className="self-center flex items-center justify-center font-mono font-bold"
              style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: RADIUS.pill, background: active ? T.activeBadge : T.ink, color: active ? T.ink : T.white, fontSize: 10 }}
            >
              {thread.unreadCount}
            </span>
          )}
        </button>

        {/* Desktop ⋯ menu — appears on hover */}
        <div ref={menuRef} className="relative hidden flex-shrink-0 items-center sm:flex">
          <button
            type="button"
            onClick={toggleMenu}
            aria-label="Conversation options"
            aria-expanded={menuOpen}
            className={`flex h-full w-9 items-center justify-center text-lg leading-none opacity-0 transition-opacity group-hover:opacity-100 ${menuOpen ? 'opacity-100' : ''}`}
            style={{ color: active ? 'rgba(255,255,255,.7)' : T.grey400 }}
          >
            ⋯
          </button>
          {menuOpen && createPortal(
            <div ref={menuPanelRef} data-thread-menu className="pr-overlay-root font-mono overflow-hidden" style={{ position: 'fixed', top: menuAt.top, right: menuAt.right, zIndex: 80, minWidth: 200, borderRadius: RADIUS.row, border: '2px solid transparent', background: fillFor('open'), color: T.ink }}>
              {menuItems.map((it, i) => (
                <button
                  key={it.label}
                  type="button"
                  onClick={() => { setMenuAt(null); it.act(); }}
                  className="block w-full text-left font-mono font-bold hover:opacity-60"
                  style={{ padding: '12px 16px', fontSize: 11, letterSpacing: '.1em', borderTop: i > 0 ? `2px solid ${T.off}` : 0, color: it.danger ? T.danger : T.ink }}
                >
                  {it.label}
                </button>
              ))}
            </div>,
            document.body,
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The padlock in the header (handoff §1): off on threads view, on (black, ring border)
 * while the secure view is open. A magenta dot says something is unread behind the
 * lock — never a count, which would leak activity.
 */
export function SecureLockButton({ secure, grow = false }) {
  const on = secure.view === 'secure';
  // Beside the + whether or not the switcher's faces are there to sit after.
  return (
    <span className="flex items-center" style={{ marginLeft: grow ? 'auto' : 4 }}>
      <button
        type="button"
        data-secure-lock
        data-on={on ? '' : undefined}
        aria-label="Secure chats"
        aria-pressed={on}
        title="Secure chats"
        onClick={secure.toggleVault}
        className="relative flex items-center justify-center flex-shrink-0"
        style={{ width: 36, height: 36, boxSizing: 'border-box', borderRadius: '50%', border: '2px solid transparent', background: on ? fillFor('on') : T.off, color: on ? T.white : T.ink, transition: 'color 200ms', cursor: 'pointer' }}
      >
        <LockIcon size={15} />
        {secure.secureUnread && (
          <span data-secure-dot aria-label="Unread secure messages" style={{ position: 'absolute', top: -2, right: -2, width: 10, height: 10, borderRadius: '50%', background: T.live, border: `2px solid ${T.white}`, boxSizing: 'content-box' }} />
        )}
      </button>
    </span>
  );
}

/**
 * The vault bar (handoff §3): black card under the search in the secure view — the
 * count, LOCK NOW, the live LOCKS IN countdown and CHANGE PIN / PATTERN. With no lock
 * set yet it says so and offers nothing else.
 */
export function VaultBar({ secure }) {
  const { session } = secure;
  const label = LOCK_TYPE_LABEL[session.type] || 'PIN';
  return (
    <div data-vault-bar className="font-mono" style={{ marginTop: 12, background: T.ink, color: T.white, borderRadius: RADIUS.row, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="flex items-center justify-between" style={{ gap: 10 }}>
        <span className="flex items-center font-bold" style={{ gap: 8, fontSize: 12, letterSpacing: '.1em' }}>
          <LockIcon size={13} />
          SECURE CHATS · {secure.secureCount}
        </span>
        {session.hasLock && (
          <button type="button" onClick={secure.lockNow} className="font-mono font-bold" style={{ height: 28, padding: '0 12px', borderRadius: RADIUS.pill, border: '2px solid transparent', background: fillFor('open'), color: T.ink, fontSize: 10, letterSpacing: '.08em', cursor: 'pointer' }}>
            LOCK NOW
          </button>
        )}
      </div>
      <div className="flex items-center justify-between" style={{ gap: 10, fontSize: 10, letterSpacing: '.08em', color: 'rgba(255,255,255,.65)' }}>
        <span data-vault-status>{session.hasLock ? `LOCKS IN ${mmss(secure.msLeft)}` : 'NO LOCK SET YET'}</span>
        {session.hasLock && (
          <button type="button" onClick={secure.changeLock} className="font-mono font-bold underline" style={{ color: T.white, background: 'none', border: 0, padding: 0, fontSize: 10, letterSpacing: '.08em', cursor: 'pointer' }}>
            CHANGE {label}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The inbox switcher: you, then every Pro page you own or staff, as round faces beside the
 * title. The one the list is showing wears the ring; a page with unread customer messages
 * carries a magenta count. Tap a page to read and answer as the page; tap yourself to come back.
 */
export function InboxSwitcher({ inboxes, activePageId, viewer, onSwitch, isMobile = false }) {
  const pages = (inboxes?.pages || []).map((p) => ({ id: p.id, name: p.name, avatarUrl: p.logoUrl, unread: p.unread || 0, page: p }));
  // You first (it is the way back), then whoever has something waiting, then the rest
  // in their usual order — so the faces that matter are the ones that never scroll away.
  const faces = [
    { id: null, name: viewer?.displayName || 'You', avatarUrl: viewer?.avatarUrl || null, unread: inboxes?.personalUnread || 0 },
    ...pages.filter((p) => p.unread > 0).sort((a, b) => b.unread - a.unread),
    ...pages.filter((p) => !(p.unread > 0)),
  ];

  const stripRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const drag = useRef(null);   // { x0, left0, moved }

  const measure = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((e) => (e.left === left && e.right === right ? e : { left, right }));
  }, []);
  useLayoutEffect(() => {
    measure();
    const el = stripRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, faces.length]);

  const scrollBy = (dx) => stripRef.current?.scrollBy({ left: dx, behavior: 'smooth' });

  // Mouse drag scrolls the strip; a drag that moved swallows the click that ends it.
  const onPointerDown = (e) => {
    if (isMobile || e.pointerType === 'touch' || e.button !== 0) return;
    drag.current = { x0: e.clientX, left0: stripRef.current.scrollLeft, moved: false };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x0;
    if (!d.moved && Math.abs(dx) < 4) return;
    if (!d.moved) { d.moved = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* older WebKit */ } }
    stripRef.current.scrollLeft = d.left0 - dx;
  };
  const swallow = useRef(false);
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    swallow.current = !!d?.moved;
    if (d?.moved) setTimeout(() => { swallow.current = false; }, 0);
  };

  const arrow = (side) => (
    <button
      type="button"
      data-faces-arrow={side}
      aria-label={side === 'right' ? 'More inboxes' : 'Previous inboxes'}
      onClick={() => scrollBy(side === 'right' ? 120 : -120)}
      className="pr-faces-arrow absolute top-1/2 flex items-center justify-center"
      style={{ [side]: 0, transform: 'translateY(-50%)', width: 22, height: 22, borderRadius: '50%', background: T.ink, color: T.white, border: 0, zIndex: 2, cursor: 'pointer', boxShadow: '0 0 0 2px #fff' }}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square"><path d={side === 'right' ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'} /></svg>
    </button>
  );
  const fade = (side) => (
    <span aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, [side]: 0, width: 22, pointerEvents: 'none', zIndex: 1, background: `linear-gradient(to ${side === 'right' ? 'left' : 'right'}, ${T.white}, rgba(255,255,255,0))` }} />
  );

  return (
    <div data-inbox-switcher-frame className="pr-faces-frame relative flex-1 min-w-0">
      {edges.left && fade('left')}
      {edges.right && fade('right')}
      {/* Desktop only, and only on hover — the .pr-faces-frame rule in MessagesPage. */}
      {!isMobile && edges.left && arrow('left')}
      {!isMobile && edges.right && arrow('right')}
      <div
        ref={stripRef}
        data-inbox-switcher
        role="tablist"
        aria-label="Inboxes"
        className="flex items-center overflow-x-auto pr-faces-strip"
        style={{ gap: 6, padding: '4px 2px', scrollbarWidth: 'none', cursor: edges.left || edges.right ? 'grab' : undefined }}
        onScroll={measure}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={(e) => { if (swallow.current) { e.stopPropagation(); e.preventDefault(); } }}
      >
        {faces.map((f) => {
          const on = (f.id || null) === (activePageId || null);
          return (
            <button
              key={f.id || 'me'}
              type="button"
              role="tab"
              aria-selected={on}
              aria-label={`${f.id ? `Inbox for ${f.name}` : 'Your inbox'}${f.unread ? `, ${f.unread} unread` : ''}`}
              title={f.id ? `Read and answer as ${f.name}` : 'Your own messages'}
              data-inbox={f.id || 'me'}
              onClick={() => onSwitch?.(f.page || null)}
              className="relative flex-shrink-0 flex items-center justify-center overflow-visible"
              style={{ width: 36, height: 36, padding: 0, borderRadius: '50%', background: 'transparent', cursor: 'pointer', ...(on ? discoFrame({ fill: T.white }) : { border: '2px solid transparent' }), opacity: on ? 1 : 0.7 }}
            >
              <span className="block overflow-hidden flex items-center justify-center font-mono font-bold" style={{ width: 28, height: 28, borderRadius: '50%', background: T.activeBadge, color: T.ink, fontSize: 11 }}>
                {f.avatarUrl ? <img src={f.avatarUrl} alt="" className="w-full h-full object-cover" draggable={false} /> : (f.name || '?').charAt(0).toUpperCase()}
              </span>
              {f.unread > 0 && (
                <span data-inbox-badge className="absolute font-mono font-bold flex items-center justify-center" style={{ top: -5, right: -6, minWidth: 16, height: 16, padding: '0 4px', borderRadius: RADIUS.pill, background: T.live, color: T.white, fontSize: 8, border: `2px solid ${T.white}`, boxSizing: 'content-box' }}>
                  {f.unread > 99 ? '99+' : f.unread}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** A real gear — Lucide `settings` (a substitute, flagged in the plan-detection handoff; the old glyph read as a sun). Stroke 2 at 16px. */
export function GearIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
